'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, Globe, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface FormData {
  title: string;
  description: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (title: string, description: string, isPublic: boolean) => void;
}

export default function CreateBoardModal({ isOpen, onClose, onCreate }: Props) {
  const [isPublic, setIsPublic] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    // Backend: Replace with Supabase insert into collections
    await new Promise((r) => setTimeout(r, 800));
    onCreate(data.title, data.description, isPublic);
    toast.success(`Board "${data.title}" berhasil dibuat! 🎉`);
    reset();
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div className="modal-content animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-800 text-primary">Buat Board Baru</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Nama Board</label>
            <input
              type="text"
              placeholder="contoh: WFC Favorit Malang"
              className={`input-field ${errors.title ? 'border-red-400' : ''}`}
              {...register('title', {
                required: 'Nama board wajib diisi',
                maxLength: { value: 50, message: 'Maksimal 50 karakter' },
              })}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1.5">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">
              Deskripsi <span className="text-muted-foreground font-400">(opsional)</span>
            </label>
            <textarea
              rows={3}
              placeholder="Ceritain sedikit tentang board ini..."
              className="input-field resize-none text-sm"
              {...register('description')}
            />
          </div>

          {/* Privacy Toggle */}
          <div>
            <p className="text-sm font-600 text-foreground mb-2">Visibilitas</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsPublic(true)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-600 transition-all ${isPublic ? 'border-accent bg-accent/5 text-accent' : 'border-border text-muted-foreground hover:border-accent/40'}`}
              >
                <Globe size={15} />
                Publik
              </button>
              <button
                type="button"
                onClick={() => setIsPublic(false)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-600 transition-all ${!isPublic ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}
              >
                <Lock size={15} />
                Privat
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {isPublic ? 'Board ini bisa dilihat semua orang.' : 'Board ini hanya bisa dilihat kamu.'}
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Batal</button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1">
              {isLoading ? <Loader2 size={16} className="animate-spin" /> : 'Buat Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}